import { post } from 'aws-amplify/api';

/**
 * UserService - Handles user management operations using AWS Amplify Admin Queries API
 * Following the documentation: https://docs.amplify.aws/gen1/react/build-a-backend/auth/admin-actions/#admin-queries-api
 */
class UserService {
  /**
   * List all users in the Cognito User Pool
   * @param {number} limit - Maximum number of users to return (default: 50)
   * @param {string} nextToken - Token for pagination
   * @returns {Promise<Object>} Users data with Users array and NextToken
   */
  async listUsers(limit = 50, nextToken = null) {
    try {
      console.log('UserService: Attempting to list users with limit:', limit);
      
      const requestData = {
        limit,
        ...(nextToken && { nextToken })
      };

      console.log('UserService: Request data:', requestData);

      const operation = post({
        apiName: 'AdminQueries',
        path: '/listUsers',
        options: {
          body: requestData
        }
      });

      const response = await operation.response;
      console.log('UserService: Got response:', response);

      const result = await response.body.json();
      console.log('UserService: Parsed result:', result);
      
      return result;
    } catch (error) {
      console.error('UserService: Error listing users:', error);
      
      // Check if it's a configuration issue
      if (error.message?.includes('AdminQueries') || error.message?.includes('API')) {
        throw new Error('Admin Queries API is not configured. Please run "amplify add api" and configure the Admin Queries API according to the Amplify documentation.');
      }
      
      throw new Error('Failed to fetch users. Please ensure you have admin privileges.');
    }
  }

  /**
   * Get a specific user by username
   * @param {string} username - The username to search for
   * @returns {Promise<Object>} User data
   */
  async getUser(username) {
    try {
      const requestData = {
        body: {
          username
        }
      };

      const response = await post({
        apiName: 'AdminQueries',
        path: '/getUser',
        options: requestData
      }).response;

      const result = await response.body.json();
      return result;
    } catch (error) {
      console.error('Error getting user:', error);
      throw new Error('Failed to fetch user details.');
    }
  }

  /**
   * Transform raw user data from Cognito into a more usable format
   * @param {Array} rawUsers - Raw user data from Cognito
   * @returns {Array} Transformed user data
   */
  transformUsers(rawUsers) {
    if (!Array.isArray(rawUsers)) {
      return [];
    }

    return rawUsers.map(user => {
      const attributes = user.Attributes ? user.Attributes.reduce((acc, attr) => {
        acc[attr.Name] = attr.Value;
        return acc;
      }, {}) : {};

      return {
        username: user.Username || '',
        email: attributes.email || '',
        name: attributes.name || attributes.given_name || '',
        familyName: attributes.family_name || '',
        fullName: `${attributes.given_name || ''} ${attributes.family_name || ''}`.trim() || 
                  attributes.name || 
                  user.Username || 
                  'Unknown User',
        status: user.UserStatus || 'UNKNOWN',
        enabled: user.Enabled !== undefined ? user.Enabled : true,
        createdDate: user.UserCreateDate ? new Date(user.UserCreateDate).toLocaleDateString() : '',
        lastModified: user.UserLastModifiedDate ? new Date(user.UserLastModifiedDate).toLocaleDateString() : '',
        isAdmin: (attributes['custom:isAdmin'] === 'true') || 
                 (attributes['cognito:groups'] && attributes['cognito:groups'].includes('admin')) ||
                 false
      };
    });
  }

  /**
   * Disable a user account
   * @param {string} username - The username to disable
   * @returns {Promise<Object>} Operation result
   */
  async disableUser(username) {
    try {
      const requestData = {
        body: {
          username
        }
      };

      const response = await post({
        apiName: 'AdminQueries',
        path: '/disableUser',
        options: requestData
      }).response;

      const result = await response.body.json();
      return result;
    } catch (error) {
      console.error('Error disabling user:', error);
      throw new Error('Failed to disable user.');
    }
  }

  /**
   * Enable a user account
   * @param {string} username - The username to enable
   * @returns {Promise<Object>} Operation result
   */
  async enableUser(username) {
    try {
      const requestData = {
        body: {
          username
        }
      };

      const response = await post({
        apiName: 'AdminQueries',
        path: '/enableUser',
        options: requestData
      }).response;

      const result = await response.body.json();
      return result;
    } catch (error) {
      console.error('Error enabling user:', error);
      throw new Error('Failed to enable user.');
    }
  }
}

export default UserService;
